import { supabase } from '../supabaseClient';

/**
 * Lab Test Offerings Service
 * Handles operations with the sparse lab_test_offerings table
 */
class LabTestOfferingsService {
  
  /**
   * Get all labs that offer a specific test
   * @param {string} testId - The test ID to search for
   * @param {Object} options - Additional options
   * @returns {Promise<Array>} Array of labs offering the test
   */
  async getLabsForTest(testId, options = {}) {
    try {
      let query = supabase
        .from('lab_test_offerings')
        .select(`
          *,
          labs (
            id,
            name,
            address,
            contact_phone,
            contact_email,
            logo_url,
            is_active
          ),
          tests (
            id,
            name,
            code,
            description
          )
        `)
        .eq('test_id', testId);

      // Apply additional filters
      if (options.minPrice) {
        query = query.gte('lab_cash_price', options.minPrice);
      }
      if (options.maxPrice) {
        query = query.lte('lab_cash_price', options.maxPrice);
      }
      if (options.sortBy === 'price') {
        query = query.order('lab_cash_price', { ascending: true });
      } else if (options.sortBy === 'name') {
        query = query.order('labs.name', { ascending: true });
      } else {
        query = query.order('lab_cash_price', { ascending: true });
      }

      const { data, error } = await query;

      if (error) throw error;

      // Filter out inactive labs
      return data.filter(offering => offering.labs.is_active);
    } catch (error) {
      console.error('Error fetching labs for test:', error);
      throw error;
    }
  }

  /**
   * Get all tests offered by a specific lab
   * @param {string} labId - The lab ID to search for
   * @param {Object} options - Additional options
   * @returns {Promise<Array>} Array of tests offered by the lab
   */
  async getTestsForLab(labId, options = {}) {
    try {
      let query = supabase
        .from('lab_test_offerings')
        .select(`
          *,
          tests (
            id,
            name,
            code,
            description,
            category,
            tube_top_color,
            preferred_specimen
          )
        `)
        .eq('lab_id', labId);

      // Apply additional filters
      if (options.category) {
        query = query.eq('tests.category', options.category);
      }
      if (options.minPrice) {
        query = query.gte('lab_cash_price', options.minPrice);
      }
      if (options.maxPrice) {
        query = query.lte('lab_cash_price', options.maxPrice);
      }
      if (options.sortBy === 'price') {
        query = query.order('lab_cash_price', { ascending: true });
      } else if (options.sortBy === 'name') {
        query = query.order('tests.name', { ascending: true });
      } else {
        query = query.order('tests.name', { ascending: true });
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching tests for lab:', error);
      throw error;
    }
  }

  /**
   * Check if a lab offers a specific test
   * @param {string} labId - The lab ID
   * @param {string} testId - The test ID
   * @returns {Promise<boolean>} True if lab offers the test
   */
  async labOffersTest(labId, testId) {
    try {
      const { data, error } = await supabase
        .from('lab_test_offerings')
        .select('id')
        .eq('lab_id', labId)
        .eq('test_id', testId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return !!data;
    } catch (error) {
      console.error('Error checking if lab offers test:', error);
      return false;
    }
  }

  /**
   * Add a test offering for a lab
   * @param {string} labId - The lab ID
   * @param {string} testId - The test ID
   * @param {Object} offeringData - The offering details
   * @returns {Promise<Object>} The created offering
   */
  async addTestOffering(labId, testId, offeringData) {
    try {
      const { data, error } = await supabase
        .from('lab_test_offerings')
        .insert({
          lab_id: labId,
          test_id: testId,
          lab_cash_price: offeringData.price,
          lab_test_code: offeringData.code,
          lab_notes: offeringData.notes
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding test offering:', error);
      throw error;
    }
  }

  /**
   * Update a test offering for a lab
   * @param {string} labId - The lab ID
   * @param {string} testId - The test ID
   * @param {Object} updateData - The updated offering details
   * @returns {Promise<Object>} The updated offering
   */
  async updateTestOffering(labId, testId, updateData) {
    try {
      const { data, error } = await supabase
        .from('lab_test_offerings')
        .update({
          lab_cash_price: updateData.price,
          lab_test_code: updateData.code,
          lab_notes: updateData.notes,
          updated_at: new Date().toISOString()
        })
        .eq('lab_id', labId)
        .eq('test_id', testId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating test offering:', error);
      throw error;
    }
  }

  /**
   * Remove a test offering for a lab
   * @param {string} labId - The lab ID
   * @param {string} testId - The test ID
   * @returns {Promise<boolean>} True if successfully removed
   */
  async removeTestOffering(labId, testId) {
    try {
      const { error } = await supabase
        .from('lab_test_offerings')
        .delete()
        .eq('lab_id', labId)
        .eq('test_id', testId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing test offering:', error);
      throw error;
    }
  }

  /**
   * Search for tests across all labs
   * @param {string} searchTerm - The search term
   * @param {Object} options - Additional options
   * @returns {Promise<Array>} Array of test offerings matching the search
   */
  async searchTestOfferings(searchTerm, options = {}) {
    try {
      let query = supabase
        .from('lab_test_offerings')
        .select(`
          *,
          labs (
            id,
            name,
            address,
            contact_phone,
            is_active
          ),
          tests (
            id,
            name,
            code,
            description,
            category
          )
        `)
        .or(`tests.name.ilike.%${searchTerm}%,tests.code.ilike.%${searchTerm}%,tests.description.ilike.%${searchTerm}%`);

      // Apply additional filters
      if (options.maxPrice) {
        query = query.lte('lab_cash_price', options.maxPrice);
      }
      if (options.category) {
        query = query.eq('tests.category', options.category);
      }

      query = query.order('lab_cash_price', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;

      // Filter out inactive labs
      return data.filter(offering => offering.labs.is_active);
    } catch (error) {
      console.error('Error searching test offerings:', error);
      throw error;
    }
  }

  /**
   * Get test offering statistics
   * @returns {Promise<Object>} Statistics about test offerings
   */
  async getOfferingStats() {
    try {
      const { data, error } = await supabase
        .from('lab_test_offerings')
        .select(`
          lab_cash_price,
          labs!inner(is_active),
          tests!inner(id)
        `);

      if (error) throw error;

      const activeOfferings = data.filter(offering => offering.labs.is_active);
      
      return {
        totalOfferings: activeOfferings.length,
        averagePrice: activeOfferings.reduce((sum, offering) => sum + offering.lab_cash_price, 0) / activeOfferings.length,
        minPrice: Math.min(...activeOfferings.map(o => o.lab_cash_price)),
        maxPrice: Math.max(...activeOfferings.map(o => o.lab_cash_price)),
        uniqueTests: new Set(activeOfferings.map(o => o.tests.id)).size,
        uniqueLabs: new Set(activeOfferings.map(o => o.labs.id)).size
      };
    } catch (error) {
      console.error('Error getting offering stats:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const labTestOfferingsService = new LabTestOfferingsService();
export default labTestOfferingsService;
